import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Shield, Clock, Send, RefreshCw, Mail, Activity, ChevronLeft, AlertTriangle } from "lucide-react";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { formatNextRun } from "@/lib/format";
import DiagnosticsCard from "@/components/admin/DiagnosticsCard";
import SchedulerJobsCard from "@/components/admin/SchedulerJobsCard";

/**
 * /admin — ops dashboard for the weekly digest pipeline.
 * Gated by `isAdmin` from AuthContext. Redirects non-admins to /dashboard.
 */
export default function Admin() {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, loading, user, authHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [running, setRunning] = useState(false);

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await axios.get(`${API}/mute-digest/scheduler-status`, { headers: authHeaders() });
      setStatus(res.data);
    } catch (err) {
      toast.error("Failed to load scheduler status");
    } finally {
      setStatusLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }
    if (!isAdmin) {
      navigate("/dashboard", { replace: true });
      return;
    }
    fetchStatus();
  }, [loading, isAuthenticated, isAdmin, navigate, fetchStatus]);

  const handleRunNow = async () => {
    if (!window.confirm("Send the weekly digest to every subscribed user with mute history right now?")) return;
    setRunning(true);
    try {
      const res = await axios.post(`${API}/mute-digest/run-now`, {}, { headers: authHeaders() });
      setLastRun({ ...res.data, at: new Date().toISOString() });
      toast.success(
        `Done — sent ${res.data.sent}, skipped ${res.data.skipped_empty + res.data.skipped_no_key}, errors ${res.data.errors}`
      );
      fetchStatus();
    } catch (err) {
      toast.error("Run-now failed");
    } finally {
      setRunning(false);
    }
  };

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <Activity className="w-8 h-8 text-[#F59E0B] animate-pulse" />
      </div>
    );
  }

  const nextRunIso = status?.job?.next_run_time;
  const trigger = status?.job?.trigger;
  const resendConfigured = lastRun?.skipped_no_key === 0 && (lastRun?.sent || 0) > 0;
  const resendUnconfigured = (lastRun?.skipped_no_key || 0) > 0;

  return (
    <div className="min-h-screen bg-[#030712] text-white">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <button
          data-testid="admin-back-btn"
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/15 border border-[#F59E0B]/30 flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#F59E0B]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Unbounded" }}>
                ADMIN
              </h1>
              <p className="text-sm text-gray-500">Operational dashboard for the weekly mute-digest pipeline</p>
            </div>
          </div>
          <div className="text-xs text-gray-600 mb-8" data-testid="admin-current-user">
            Signed in as <span className="text-gray-300">{user?.email}</span>
          </div>
        </motion.div>

        {resendUnconfigured && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass-effect mb-6 p-4 rounded-xl border border-[#FF3B30]/30 bg-gradient-to-r from-[#FF3B30]/5 to-transparent flex items-start gap-3"
            data-testid="admin-resend-warning"
          >
            <AlertTriangle className="w-5 h-5 text-[#FF3B30] shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-white">Resend API key not configured</div>
              <p className="text-xs text-gray-400 mt-1">
                The job ran but every send hit <span className="text-gray-200 mono">skipped_no_key</span>. Add{" "}
                <span className="text-gray-200 mono">RESEND_API_KEY</span> to{" "}
                <span className="text-gray-200 mono">/app/backend/.env</span> and restart the backend.
              </p>
            </div>
          </motion.div>
        )}

        <div className="mb-6">
          <DiagnosticsCard authHeaders={authHeaders} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2 mb-6">
          <Card data-testid="admin-status-card" className="glass-effect border-white/5">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-3">
                <Clock className="w-3 h-3" /> Scheduler
              </div>
              {statusLoading || !status ? (
                <div className="text-gray-500 text-sm">Loading…</div>
              ) : (
                <>
                  <div className="text-2xl font-bold text-white" data-testid="admin-next-run">
                    {nextRunIso ? formatNextRun(nextRunIso) : "—"}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Next run · {nextRunIso ? new Date(nextRunIso).toLocaleString() : "no scheduled job"}
                  </div>
                  {trigger && (
                    <div className="mt-3 text-[10px] text-gray-600 mono break-all">{trigger}</div>
                  )}
                </>
              )}
              <Button
                data-testid="admin-refresh-btn"
                size="sm"
                variant="ghost"
                onClick={fetchStatus}
                disabled={statusLoading}
                className="mt-3 text-xs text-gray-400 hover:text-white"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${statusLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </CardContent>
          </Card>

          <Card
            data-testid="admin-last-run-card"
            className={`glass-effect border-white/5 ${resendConfigured ? "border-[#00FFA3]/20" : ""}`}
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-500 mb-3">
                <Mail className="w-3 h-3" /> Last manual run
              </div>
              {!lastRun ? (
                <div className="text-gray-500 text-sm">No manual run this session</div>
              ) : (
                <>
                  <div className="grid grid-cols-4 gap-3">
                    <Stat label="Sent" value={lastRun.sent} accent="text-[#00FFA3]" />
                    <Stat label="Empty" value={lastRun.skipped_empty} />
                    <Stat label="No key" value={lastRun.skipped_no_key} accent={lastRun.skipped_no_key ? "text-[#F59E0B]" : ""} />
                    <Stat label="Errors" value={lastRun.errors} accent={lastRun.errors ? "text-[#FF3B30]" : ""} />
                  </div>
                  <div className="text-[10px] text-gray-600 mt-3">{new Date(lastRun.at).toLocaleString()}</div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="mb-6">
          <SchedulerJobsCard jobs={status?.jobs} />
        </div>

        <Card className="glass-effect border-white/5">
          <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div>
              <div className="text-sm font-bold text-white">Send weekly digest now</div>
              <p className="text-xs text-gray-500 mt-1 max-w-sm">
                Fans the digest out to every subscribed user. Same code path as the Monday cron — safe to run on demand.
              </p>
            </div>
            <Button
              data-testid="admin-run-now-btn"
              onClick={handleRunNow}
              disabled={running}
              className="bg-[#F59E0B] text-black hover:bg-[#F59E0B]/90 font-semibold disabled:opacity-50"
            >
              <Send className={`w-4 h-4 mr-2 ${running ? "animate-pulse" : ""}`} />
              {running ? "Sending…" : "Send to all users"}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Stat({ label, value, accent = "text-white" }) {
  return (
    <div>
      <div className={`text-xl font-bold mono ${accent}`}>{value ?? 0}</div>
      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}
